import React from 'react';
import { Route, Switch, Link, RouteComponentProps } from 'react-router-dom';
import { Observe } from 'rewire-core';
import { WithStyle, ToggleMenu, IToggleMenuItem } from 'rewire-ui';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import ListAltIcon from '@material-ui/icons/ListAlt';
import TuneIcon from '@material-ui/icons/Tune';
import CardModeIcon from '@material-ui/icons/ViewModule';
import ListModeIcon from '@material-ui/icons/ViewList';
import { DeepPartial, IGroup } from '../DataTypes';
import ErrorPage from '../ErrorPage';
import Routes from '../Routes';
import areaModel, { GroupDisplayMode } from './AreaModel';
import mainPageModel from './MainPageModel';
import { RouteManagerBase } from './RouteManager';
import Search from './Search';
import { withStyles } from '../utility/styles';

const areaStyles = (_theme: Theme) => ({});

interface IAreaProps {}

type AreaProps = WithStyle<ReturnType<typeof areaStyles>, IAreaProps>;

class Area extends React.Component<AreaProps> {
  constructor(props: AreaProps) {
    super(props);
  }

  render() {
    return (
      <Switch>
        <Route exact path={Routes.Area} component={AreaInternal} />
        <Route exact path={Routes.SearchArea} component={Search} />
        <Route component={ErrorPage} />
      </Switch>
    );
  }
}

const areaInternalStyles = (_theme: Theme) => ({
  root: {
    display: 'flex',
    flex: '1',
    flexDirection: 'column',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: '0',
  },
  cardGroupsContainer: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: '20px',
  },
  cardContainer: {
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    alignItems: 'center',
    marginLeft: '32px',
    marginBottom: '24px',
  },
  cardGroupButton: {
    width: '90px',
    height: '90px',
    marginBottom: '10px',
    borderRadius: '20px',
    '& svg': {
      fontSize: '2em',
    },
  },
  listGroupsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    marginTop: '10px',
  },
  addCardGroupButton: {
    borderStyle: 'dashed',
    '& svg': {
      fontSize: '2.4em',
    },
  },
  listItemGroupButton: {
    flex: '0 0 auto',
    padding: '4px 8px',
    marginBottom: '5px',
    '& svg': {
      marginRight: '10px',
      fontSize: '2em',
    },
  },
  addListItemGroupButton: {},
  modeSelectButton: {
    padding: '0px',
    minWidth: '0px',
    marginLeft: '100px',
    marginRight: '5px',
    // '&:hover, &:active': {
    //   background: 'transparent',
    // },
  },
  areaSettingsButton: {
    // backgroundColor: 'transparent !important',
  },
  groupTitle: {
    width: '90px',
    textAlign: 'center',
  },
});

interface IAreaInternalProps {
  areaId: string;
}

interface IAreaInternalState {}

type AreaInternalProps = WithStyle<ReturnType<typeof areaInternalStyles>, IAreaInternalProps & RouteComponentProps<any>>;

const AreaInternal = withStyles(
  areaInternalStyles,
  class extends React.Component<AreaInternalProps, IAreaInternalState> {
    items: IToggleMenuItem[];

    constructor(props: AreaInternalProps) {
      super(props);

      areaModel.load(props.match.params.areaId);
      this.items = [
        { name: 'cards', title: 'View as Cards', icon: CardModeIcon, active: () => GroupDisplayMode.Cards === areaModel.mode },
        { name: 'list', title: 'View as List', icon: ListModeIcon, active: () => GroupDisplayMode.List === areaModel.mode },
      ];
    }

    componentWillUnmount() {
      areaModel.reset();
    }

    componentDidMount() {
      areaModel.updateTitle();
    }

    UNSAFE_componentWillReceiveProps(nextProps: AreaInternalProps) {
      const nextAreaId: string = nextProps.match.params.areaId;
      const currAreaId: string = this.props.match.params.areaId;

      if (nextAreaId !== currAreaId) {
        areaModel.load(nextAreaId);
      }
    }

    handleAddGroupClick = () => {
      areaModel.showAddGroupDialog();
    };

    handleAreaSettingsClick = () => {
      areaModel.showAreaSettingsDialog();
    };

    handleItemClick = (item: IToggleMenuItem) => {
      areaModel.setMode(item.name as GroupDisplayMode);
    };

    renderNewGroupCard(): JSX.Element {
      return (
        <div className={this.props.classes.cardContainer}>
          <Button
            variant="outlined"
            className={classNames(this.props.classes.addCardGroupButton, this.props.classes.cardGroupButton)}
            onClick={this.handleAddGroupClick}
          >
            <AddIcon />
          </Button>
          <Typography variant="body2" className={this.props.classes.groupTitle}>
            <strong>Add Group</strong>
          </Typography>
        </div>
      );
    }

    renderCard(group: DeepPartial<IGroup>): JSX.Element | null {
      if (!areaModel.areaId) return null;

      const groupLinkData = mainPageModel.groupMap[group.id!].groupLinkData;
      return (
        <div key={group.id} className={this.props.classes.cardContainer}>
          <Button
            variant="outlined"
            className={this.props.classes.cardGroupButton}
            component={Link}
            to={RouteManagerBase.current.createLinkFor(groupLinkData)}
          >
            <ListAltIcon />
          </Button>
          <Typography variant="body2" className={this.props.classes.groupTitle}>
            <strong>{group.name}</strong>
          </Typography>
        </div>
      );
    }

    renderNewGroupListItem(): JSX.Element {
      return (
        <Button
          className={classNames(this.props.classes.addListItemGroupButton, this.props.classes.listItemGroupButton)}
          onClick={this.handleAddGroupClick}
        >
          <AddIcon />{' '}
          <Typography variant="body2">
            <strong>Add Group</strong>
          </Typography>
        </Button>
      );
    }

    renderListItem(group: DeepPartial<IGroup>): JSX.Element | undefined {
      if (!areaModel.areaId) return;

      const groupLinkData = mainPageModel.groupMap[group.id!].groupLinkData;
      return (
        <Button
          key={group.id}
          className={this.props.classes.listItemGroupButton}
          component={Link}
          to={RouteManagerBase.current.createLinkFor(groupLinkData)}
        >
          <ListAltIcon />{' '}
          <Typography variant="body2">
            <strong>{group.name}</strong>
          </Typography>
        </Button>
      );
    }

    renderDisplayMenu(): JSX.Element {
      const { classes } = this.props;
      const buttonContent = <TuneIcon />;

      return (
        <ToggleMenu
          menuId="area-group-display-options"
          buttonContent={buttonContent}
          buttonProps={{ disableRipple: true }}
          items={this.items}
          onItemClick={this.handleItemClick}
          classes={{ menuButton: classes.modeSelectButton }}
        />
      );
    }

    renderContentCards(): JSX.Element {
      const { classes } = this.props;

      return (
        <div className={classes.cardGroupsContainer}>
          {this.renderNewGroupCard()}
          {areaModel.groups.map((group) => this.renderCard(group))}
        </div>
      );
    }

    renderContentList(): JSX.Element {
      const { classes } = this.props;

      return (
        <div className={classes.listGroupsContainer}>
          {this.renderNewGroupListItem()}
          {areaModel.groups.map((group) => this.renderListItem(group))}
        </div>
      );
    }

    render() {
      const { classes } = this.props;

      return (
        <Observe
          render={() => (
            <div className={classes.root}>
              <div className={classes.titleContainer}>
                <Typography variant="h5">{areaModel.areaName} Groups</Typography>
                {this.renderDisplayMenu()}
              </div>
              {areaModel.mode === GroupDisplayMode.Cards ? this.renderContentCards() : this.renderContentList()}
            </div>
          )}
        />
      );
    }
  },
);

export default withStyles<IAreaProps>(areaStyles, Area);
